import {
    Box,
    FormControl,
    FormHelperText,
    MenuItem,
    Radio,
    Select,
    Typography
} from "@mui/material";
import {
    DayOfWeek,
    MonthlyRecurrence,
    MonthlyScheduleType,
    NthWeekDay
} from "../../models";
import React, {
    Dispatch,
    SetStateAction
} from "react";

import _ from "lodash";

type MonthlyRecurrenceFormProps = {
    monthlyRecurrence: MonthlyRecurrence;
    setMonthlyRecurrence: Dispatch<SetStateAction<MonthlyRecurrence>>;
    error: (Record<keyof Omit<MonthlyRecurrence, "nthWeekDay">, string> & Record<"nthWeekDay", Record<keyof NthWeekDay, string>>) | undefined;
};

export const MonthlyRecurrenceForm = ({ monthlyRecurrence, setMonthlyRecurrence, error }: MonthlyRecurrenceFormProps) => {

    return (
        <>
            <Typography>Every</Typography>
            <Box display="flex" gap={1} alignItems="center">
                <Select
                    id="monthlyInterval"
                    name="monthlyInterval"
                    value={monthlyRecurrence?.interval ?? 1}
                    onChange={(e) => setMonthlyRecurrence(prev => ({ ...prev, interval: parseInt(e.target.value as string) }))}
                    variant="standard"
                    MenuProps={{
                        sx: {
                            maxHeight: 250
                        }
                    }}
                >
                    {Array(12).fill(0).map((_, i) => (
                        <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                    ))}
                </Select>
                <Typography>month(s)</Typography>
            </Box>
            <Box display="flex" gap={1} alignItems="center" justifyContent="space-between">
                <Typography>On</Typography>
                <Radio
                    checked={monthlyRecurrence.type === MonthlyScheduleType.DAY_OF_MONTH}
                    onChange={(e) => setMonthlyRecurrence(prev => ({ ...prev, type: MonthlyScheduleType.DAY_OF_MONTH }))}
                    size="small"
                />
            </Box>
            <Box display="flex" gap={1} alignItems="center">
                <FormControl size="small" variant="standard">
                    <Select
                        id="dayOfMonth"
                        name="dayOfMonth"
                        value={monthlyRecurrence.dayOfMonth ?? ""}
                        onChange={(e) => setMonthlyRecurrence(prev => ({ ...prev, dayOfMonth: e.target.value }))}
                        variant="standard"
                        MenuProps={{
                            sx: {
                                maxHeight: 250
                            }
                        }}
                        error={!!error?.dayOfMonth}
                    >
                        {Array(31).fill(0).map((_, i) => (
                            <MenuItem key={i + 1} value={(i + 1).toString()}>{i + 1}</MenuItem>
                        ))}
                        <MenuItem value="L">Last</MenuItem>
                    </Select>
                    <FormHelperText error>{error?.dayOfMonth}</FormHelperText>
                </FormControl>
                <Typography>day</Typography>
            </Box>
            <Box display="flex" justifyContent="flex-end">
                <Radio
                    checked={monthlyRecurrence?.type === MonthlyScheduleType.NTH_WEEK_DAY}
                    onChange={(e) => setMonthlyRecurrence(prev => ({ ...prev, type: MonthlyScheduleType.NTH_WEEK_DAY }))}
                    size="small"
                />
            </Box>
            <Box display="flex" gap={1} alignItems="center">
                <FormControl size="small" variant="standard" error={!!error?.nthWeekDay?.weekNumber}>
                    <Select
                        id="weekNumber"
                        name="weekNumber"
                        value={monthlyRecurrence?.nthWeekDay?.weekNumber ?? ""}
                        onChange={(e) => setMonthlyRecurrence(prev => ({
                            ...prev, nthWeekDay: { ...prev.nthWeekDay, weekNumber: e.target.value as string }
                        }))}
                    >
                        <MenuItem value="1">1st</MenuItem>
                        <MenuItem value="2">2nd</MenuItem>
                        <MenuItem value="3">3rd</MenuItem>
                        <MenuItem value="4">4th</MenuItem>
                        <MenuItem value="L">Last</MenuItem>
                    </Select>
                    <FormHelperText error>{error?.nthWeekDay?.weekNumber}</FormHelperText>
                </FormControl>
                <FormControl size="small" variant="standard" error={!!error?.nthWeekDay?.day}>
                    <Select
                        id="dayOfWeek"
                        name="dayOfWeek"
                        value={monthlyRecurrence?.nthWeekDay?.day ?? ""}
                        onChange={(e) => setMonthlyRecurrence(prev => ({
                            ...prev, nthWeekDay: { ...prev.nthWeekDay, day: e.target.value as DayOfWeek }
                        }))}
                        variant="standard"
                    >
                        {Object.values(DayOfWeek).map((day) => (
                            <MenuItem key={day} value={day}>{_.capitalize(day)}</MenuItem>
                        ))}
                    </Select>
                    <FormHelperText error>{error?.nthWeekDay?.day}</FormHelperText>
                </FormControl>
            </Box>
        </>
    );
};